import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactSelect from 'react-select';
import { ClearIndicator, DropdownIndicator, SingleValue } from './components';
import { convertOptionsToArray } from './helpers/utils';
import { FormLabelPopover } from '@pattern-library/message-popover';
import { FormInputErrors, FormInputLabel } from '@pattern-library/form-input';
const propTypes = {
  /**
   * Unique DOM ID for the select input
   */
  id: PropTypes.string.isRequired,
  /**
   * the label for the select input
   */
  label: PropTypes.string.isRequired,
  /**
   * array or object of select options
   */
  options: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
  /**
   * Override the components for the react-select component
   */
  componentsOverride: PropTypes.object,
  /**
   * sets the initial selected option to the provided value
   */
  defaultValue: PropTypes.string,
  /**
   * The severity of the input's error state
   */
  errorLevel: PropTypes.oneOf(['error', 'warning']),
  /**
   * Error message(s) to display when `hasError` is enabled
   */
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /**
   * render the component's error state
   */
  hasError: PropTypes.bool,
  /**
   * Do options have dividers between them
   */
  hasOptionDividers: PropTypes.bool,
  /**
   * accessibly hide the field's form label
   */
  hideLabel: PropTypes.bool,
  /**
   * Content to display in message popover component
   */
  infoPopoverContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Additional props to pass to the message popover component
   */
  infoPopoverProps: PropTypes.object,
  /**
   * Additional classes for the input element
   */
  inputClass: PropTypes.string,
  /**
   * Is there a clear button
   */
  isClearable: PropTypes.bool,
  /**
   * disables the form field
   */
  isDisabled: PropTypes.bool,
  /**
   * sets the select field to required="true"
   */
  isRequired: PropTypes.bool,
  /**
   * Is the select searchable
   */
  isSearchable: PropTypes.bool,
  /**
   * the `name` attribute for the input element
   */
  name: PropTypes.string,
  /**
   * a callback function to be executed when the value of the select field changes
   */
  onChange: PropTypes.func,
  /**
   * Appended to the text label.
   * Defaults to (optional) for non-required inputs, but can be overridden.
   */
  optionalInputLabel: PropTypes.string,
  /**
   * Used to display in select before selection is made
   * showEmptyOption must be enabled.
   */
  placeholder: PropTypes.string,
  /**
   * Additional props for the react-select component
   * https://react-select.com/props
   */
  reactSelectProps: PropTypes.object,
  /**
   * Component to use: ReactSelect (default) or AsyncSelect
   */
  SelectComponent: PropTypes.elementType,
  /**
   * if set to true, an empty option (`<option value="">--</option>`)
   * will be added as the first option
   */
  showEmptyOption: PropTypes.bool,
  /**
   * Append asterisk to label
   */
  showRequiredAsterisk: PropTypes.bool,
  /**
   * Text message shown below the input
   */
  subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Whether the component uses the new styles and icons
   */
  useNextUI: PropTypes.bool,
  /**
   * use this prop to fully control the form
   * this prop must be the value of one of the provided options
   */
  value: PropTypes.string
};
const defaultProps = {
  defaultValue: undefined,
  errorLevel: 'error',
  errorMessage: undefined,
  hasOptionDividers: false,
  hideLabel: false,
  infoPopoverContent: null,
  infoPopoverProps: {},
  inputClass: '',
  isClearable: false,
  isRequired: true,
  isSearchable: false,
  onChange: () => {},
  optionalInputLabel: '(optional)',
  placeholder: undefined,
  SelectComponent: ReactSelect,
  showEmptyOption: false,
  showRequiredAsterisk: false,
  useNextUI: true,
  value: undefined
};
function Select(props) {
  const {
    componentsOverride,
    defaultValue,
    errorLevel,
    errorMessage,
    hasError,
    hasOptionDividers,
    hideLabel,
    id,
    infoPopoverContent,
    infoPopoverProps,
    inputClass,
    isClearable,
    isDisabled,
    isRequired,
    isSearchable,
    label,
    name,
    onChange,
    optionalInputLabel,
    options,
    placeholder,
    reactSelectProps,
    SelectComponent,
    showEmptyOption,
    showRequiredAsterisk,
    subtext,
    useNextUI,
    value
  } = props;
  const wrapperClasses = classNames('asc-form-group', inputClass, {
    'is-disabled': isDisabled,
    'is-searchable': isSearchable,
    [`asc-form-input--${errorLevel}`]: hasError,
    'asc-data-grid-select': !useNextUI
  });
  const containerClasses = classNames('asc-select-container', {
    'has-dividers': hasOptionDividers
  });
  const optionsArray = useMemo(() => {
    const array = Array.isArray(options) ? options : convertOptionsToArray(options);
    if (showEmptyOption) {
      const emptyOption = {
        value: '',
        label: placeholder || '--'
      };
      return [emptyOption, ...array];
    } else {
      return array;
    }
  }, [options, showEmptyOption, placeholder]);
  const findOptionByValue = useCallback(v => {
    if (typeof v === 'undefined') return undefined;
    return optionsArray.find(o => o.value === v);
  }, [optionsArray]);
  const customComponents = {
    DropdownIndicator,
    ClearIndicator,
    SingleValue,
    ...componentsOverride
  };
  const defaultValueObj = findOptionByValue(defaultValue);
  const valueObj = findOptionByValue(value);
  const hasInfoPopover = infoPopoverContent !== null;
  const ids = {
    help: [`${id}-help`, hasError && Boolean(errorMessage)],
    subtext: [`${id}-subtext`, Boolean(subtext)],
    popover: [`${id}-popover`, hasInfoPopover]
  };
  const ariaDescribedByIds = Object.keys(ids).filter(key => ids[key][1]).map(key => ids[key][0]);
  const hiddenRef = useRef();
  const [currentValue, setCurrentValue] = useState(valueObj?.value);
  const [hasChanged, setHasChanged] = useState(false);
  const handleSelectChange = newValue => {
    let value = newValue?.value;
    if (typeof value === 'object') {
      value = JSON.stringify(value);
    }
    setCurrentValue(value);
    onChange(newValue?.value);
    setHasChanged(true);
  };
  useEffect(() => {
    if (hasChanged) {
      const event = new Event('change', {
        bubbles: true
      });
      hiddenRef.current.dispatchEvent(event);
    }
  }, [currentValue, hasChanged]);
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses
  }, /*#__PURE__*/React.createElement(FormInputLabel, {
    hideLabel: hideLabel,
    inputId: id,
    isRequired: isRequired,
    label: label,
    optionalInputLabel: optionalInputLabel,
    showRequiredAsterisk: showRequiredAsterisk
  }), hasInfoPopover && /*#__PURE__*/React.createElement(FormLabelPopover, {
    messagePopoverId: ids.popover[0],
    messagePopoverContent: infoPopoverContent,
    messagePopoverProps: infoPopoverProps
  }), /*#__PURE__*/React.createElement(SelectComponent, _extends({
    className: containerClasses,
    classNamePrefix: "asc-select",
    components: customComponents,
    defaultValue: defaultValueObj,
    inputId: id,
    isClearable: isClearable,
    isDisabled: isDisabled,
    isSearchable: isSearchable,
    name: `${name}-select`,
    onChange: newValue => handleSelectChange(newValue),
    options: optionsArray,
    placeholder: placeholder,
    required: isRequired,
    useNextUI: useNextUI,
    value: valueObj,
    "aria-describedby": ariaDescribedByIds.length ? ariaDescribedByIds.join(' ') : null
  }, reactSelectProps)), /*#__PURE__*/React.createElement("select", {
    ref: hiddenRef,
    readOnly: true,
    name: name,
    value: currentValue,
    "aria-hidden": "true",
    className: "visually-hide",
    tabIndex: -1
  }, /*#__PURE__*/React.createElement("option", {
    value: currentValue
  }, currentValue)), /*#__PURE__*/React.createElement(FormInputErrors, {
    containerId: ids.help[0],
    errorLevel: errorLevel,
    errorMessage: errorMessage,
    hasError: hasError
  }), subtext && /*#__PURE__*/React.createElement("p", {
    id: ids.subtext[0],
    className: "asc-form-subtext"
  }, subtext));
}
Select.propTypes = propTypes;
Select.defaultProps = defaultProps;
export default Select;